import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/apiUrl';
import { User } from '../types';
import { initializeSessionSecurity, validateCurrentSession, forceSessionCleanup } from '../utils/sessionSecurity';

// Configure axios interceptor for authentication
axios.interceptors.request.use((config) => {
    const tempToken = sessionStorage.getItem('temp_client_token');
    const tempUsername = sessionStorage.getItem('temp_client_username');
    const token = sessionStorage.getItem('token');
    
    // Only use temporary token if we're explicitly in temporary access mode
    const isTemporaryAccess = window.location.search.includes('temp_access=true');
    
    if (tempToken && isTemporaryAccess) {
        // Verify the token is actually for temporary access
        try {
            const tokenParts = tempToken.split('.');
            if (tokenParts.length === 3) {
                const base64Payload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
                const tokenPayload = JSON.parse(atob(base64Payload));
                
                // Only use temporary token if it's actually marked as temporary
                if (tokenPayload.isTemporary || tokenPayload.isSuperAdminViewer) {
                    // Using temporary token for API call
                    config.headers.Authorization = `Bearer ${tempToken}`;
                    return config;
                } else {
                    console.warn('Token exists but not for temporary access');
                    sessionStorage.removeItem('temp_client_token');
                    sessionStorage.removeItem('temp_client_username');
                }
            }
        } catch (error) {
            console.warn('Failed to validate temporary token, clearing');
            sessionStorage.removeItem('temp_client_token');
            sessionStorage.removeItem('temp_client_username');
        }
    }
    
    // Use regular token for all other cases
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
});

interface AuthContextType {
    isAuthenticated: boolean;
    user: User | null;
    loading: boolean;
    login: (token: string, redirectPath?: string) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Session validation
// Temporarily disabled to prevent logout on refresh
// const validateSession = async (token: string): Promise<boolean> => {
//     try {
//         const response = await axios.post(
//             `${process.env.REACT_APP_API_URL}/api/auth/validate-session`,
//             {},
//             { headers: { Authorization: `Bearer ${token}` } }
//         );
//         return response.status === 200;
//     } catch (error) {
//         console.warn('Session validation failed:', error);
//         return false;
//     }
// };

// Generate unique session ID for this browser context
const generateSessionId = () => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Security: Check if this is a new browser session
// const isNewBrowserSession = (): boolean => {
//     const sessionStartTime = sessionStorage.getItem('sessionStartTime');
//     if (!sessionStartTime) {
//         // This is a new browser session
//         sessionStorage.setItem('sessionStartTime', Date.now().toString());
//         return true;
//     }
//     return false;
// };

// Temporarily disabled - too aggressive
// const clearAuthOnNewSession = () => {
//     if (isNewBrowserSession()) {
//         console.log('🔒 New browser session detected - clearing any existing auth data');
//         sessionStorage.removeItem('token');
//         sessionStorage.removeItem('user');
//         sessionStorage.removeItem('sessionId');
//         localStorage.removeItem('token');
//         localStorage.removeItem('user');
//         localStorage.removeItem('sessionId');
//     }
// };

// Security: Detect browser session changes
const detectBrowserSessionChange = () => {
    // Check if this is a new browser session or if the page was refreshed
    const currentSessionStart = sessionStorage.getItem('sessionStartTime');
    const lastKnownSession = sessionStorage.getItem('lastKnownSession');
    
    if (!currentSessionStart) {
        // New browser session
        sessionStorage.setItem('sessionStartTime', Date.now().toString());
        sessionStorage.setItem('lastKnownSession', Date.now().toString());
        return 'new_session';
    }
    
    if (!lastKnownSession || currentSessionStart !== lastKnownSession) {
        // Session changed (refresh or new tab)
        sessionStorage.setItem('lastKnownSession', currentSessionStart);
        return 'session_changed';
    }
    
    return 'same_session';
};

// Security: Enhanced session cleanup
const enhancedSessionCleanup = () => {
    const sessionStatus = detectBrowserSessionChange();
    
    if (sessionStatus === 'new_session') {
        // Only clear if there's no valid token - don't clear on simple refresh
        const token = sessionStorage.getItem('token');
        if (!token) {
            sessionStorage.clear();
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('sessionId');
        }
    }
    // For session_changed (refresh), keep existing session
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [currentSessionId] = useState(generateSessionId());

    // Suppress 401 errors from invalidate-session endpoint
    useEffect(() => {
        const originalFetch = window.fetch;
        const originalXHROpen = XMLHttpRequest.prototype.open;
        const originalXHRSend = XMLHttpRequest.prototype.send;

        // Override fetch to suppress 401 errors from invalidate-session
        window.fetch = async (...args) => {
            try {
                const response = await originalFetch(...args);
                const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url;
                
                // Suppress 401 errors from invalidate-session endpoint
                if (url.includes('/api/auth/invalidate-session') && response.status === 401) {
                    // Don't log or throw error for expected 401 responses
                    return response;
                }
                
                return response;
            } catch (error) {
                const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url;
                if (url.includes('/api/auth/invalidate-session')) {
                    // Don't log errors for invalidate-session requests
                    return new Response(null, { status: 401 });
                }
                throw error;
            }
        };

        // Override XMLHttpRequest to suppress 401 errors from invalidate-session
        XMLHttpRequest.prototype.open = function(method: string, url: string | URL, async?: boolean, username?: string | null, password?: string | null) {
            (this as any)._isInvalidateSession = typeof url === 'string' && url.includes('/api/auth/invalidate-session');
            return originalXHROpen.call(this, method, url, async ?? true, username, password);
        };

        XMLHttpRequest.prototype.send = function(data?: Document | XMLHttpRequestBodyInit | null) {
            const xhr = this as any;
            const originalOnReadyStateChange = xhr.onreadystatechange;
            
            xhr.onreadystatechange = function(this: XMLHttpRequest, ev: Event) {
                if (xhr.readyState === 4 && xhr._isInvalidateSession && xhr.status === 401) {
                    // Suppress 401 errors for invalidate-session requests
                    return;
                }
                
                if (originalOnReadyStateChange) {
                    originalOnReadyStateChange.call(this, ev);
                }
            };
            
            return originalXHRSend.call(this, data);
        };

        return () => {
            window.fetch = originalFetch;
            XMLHttpRequest.prototype.open = originalXHROpen;
            XMLHttpRequest.prototype.send = originalXHRSend;
        };
    }, []);

    useEffect(() => {
        const initializeAuth = async () => {
            try {
                // Only initialize session security if we don't have a valid session
                // This prevents unnecessary security checks on every page load
                const token = sessionStorage.getItem('token');
                const userDataStr = sessionStorage.getItem('user');
                const sessionId = sessionStorage.getItem('sessionId');
                
                // Skip session security on login/auth pages
                const isAuthPage = window.location.pathname.includes('/login') || 
                                 window.location.pathname.includes('/signup') || 
                                 window.location.pathname.includes('/password-reset');
                
                if (isAuthPage) {
                    // Auth page detected - skipping session security
                    setLoading(false);
                    return;
                }
                
                // If we have a valid session, skip the aggressive security check
                if (token && userDataStr && sessionId) {
                    // Valid session detected - skipping aggressive security check
                    // Just validate the existing session without full security initialization
                    if (!validateCurrentSession()) {
                        // Existing session validation failed - clearing auth
                        forceSessionCleanup();
                        const currentPath = window.location.pathname;
                        
                        // CRITICAL: Check if we're on a company-specific route before redirecting
                        const pathParts = currentPath.split('/');
                        const companySlug = pathParts[1];
                        const isCompanySpecific = companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password', 'thank-you'].includes(companySlug);
                        
                        let redirectTo;
                        // Check if we have a saved login URL for this company
                        const savedLoginUrl = sessionStorage.getItem('lastLoginUrl');
                        if (savedLoginUrl && companySlug && isCompanySpecific) {
                            // Extract company slug from saved URL and redirect directly to company login
                            const savedPathParts = savedLoginUrl.split('/');
                            const savedCompanySlug = savedPathParts[1];
                            if (savedCompanySlug === companySlug) {
                                redirectTo = `/${companySlug}/login`;
                            } else {
                                redirectTo = `/${companySlug}/login`;
                            }
                        } else if (isCompanySpecific) {
                            redirectTo = `/${companySlug}/login`;
                        } else {
                            redirectTo = currentPath.includes('/client/') ? '/client-signup' : '/admin/login';
                        }
                        
                        clearAuthData(redirectTo);
                        return;
                    }
                } else {
                    // CRITICAL: Check if we're in the middle of a logout process
                    // If we're on a company-specific route, don't redirect to /client-signup
                    const currentPath = window.location.pathname;
                    const pathParts = currentPath.split('/');
                    const companySlug = pathParts[1];
                    const isCompanySpecific = companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password', 'thank-you'].includes(companySlug);
                    
                    if (isCompanySpecific) {
                        setLoading(false);
                        return;
                    }
                    
                    // Only run full session security if we don't have a session and we're not on a company-specific route
                    // TEMPORARILY DISABLED for local development debugging
                    // initializeSessionSecurity();
                }
                
                // Security: Enhanced session cleanup and validation
                enhancedSessionCleanup();
                
                // Check for temporary access token in URL first, then sessionStorage
                const urlToken = new URLSearchParams(window.location.search).get('token');
                const tempToken = urlToken || sessionStorage.getItem('temp_client_token');
                const tempUsername = sessionStorage.getItem('temp_client_username') || 'super-admin-viewer';
                
                // If we have a token from URL, store it in sessionStorage for future use
                if (urlToken && !sessionStorage.getItem('temp_client_token')) {
                    sessionStorage.setItem('temp_client_token', urlToken);
                }
                
                // Handle temporary super admin viewer access
                if (tempToken && window.location.search.includes('temp_access=true')) {
                    
                    try {
                        // Decode the temp token to get actual user data
                        // JWT tokens have 3 parts: header.payload.signature
                        const tokenParts = tempToken.split('.');
                        if (tokenParts.length === 3) {
                            // The payload is base64url encoded, convert to base64 and decode
                            const base64Payload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
                            const tokenPayload = JSON.parse(atob(base64Payload));
                            
                            
                            // Verify this is actually a temporary access token
                            if (!tokenPayload.isTemporary && !tokenPayload.isSuperAdminViewer) {
                                console.warn('🔒 Token exists but is not for temporary access - clearing');
                                sessionStorage.removeItem('temp_client_token');
                                sessionStorage.removeItem('temp_client_username');
                                // Fall through to regular authentication
                            } else {
                                // Use the actual username from the token payload
                                const actualUsername = tokenPayload.username || tempUsername;
                                
                                // Generate a unique session ID for this admin viewer session
                                // Include window/tab identifier to prevent conflicts when multiple tabs are open
                                const windowId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                                const sessionId = `super_admin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                                sessionStorage.setItem('super_admin_session_id', sessionId);
                                sessionStorage.setItem('window_id', windowId);
                                
                                // Extract admin username from token payload
                                const adminUsername = tokenPayload.adminUsername || 'Super Admin';
                                
                                setUser({
                                    _id: tokenPayload.id || 'temp',
                                    id: tokenPayload.id || 'temp',
                                    username: actualUsername,
                                    clientName: `Super Admin Viewer (${adminUsername})`,
                                    email: tokenPayload.email || '',
                                    role: tokenPayload.role || 'super_admin_viewer',
                                    isActive: true,
                                    createdAt: new Date().toISOString(),
                                    companyName: tokenPayload.companyName,
                                    isSuperAdminViewer: tokenPayload.isSuperAdminViewer || false,
                                    adminUsername: adminUsername,
                                    themePreference: tokenPayload.themePreference || 'light',
                                    branding: tokenPayload.branding || {},
                                    sessionId: sessionId // Add unique session ID
                                });
                                setIsAuthenticated(true);
                                setLoading(false);
                                // console.log('[DEBUG] Super Admin Viewer access:', { 
                                //     username: actualUsername, 
                                //     role: tokenPayload.role,
                                //     company: tokenPayload.companyName,
                                //     sessionId: sessionId,
                                //     windowId: windowId
                                // });
                                return;
                            }
                        } else {
                            console.error('Invalid JWT token format');
                            sessionStorage.removeItem('temp_client_token');
                            sessionStorage.removeItem('temp_client_username');
                        }
                    } catch (error) {
                        console.error('Error parsing temp access data:', error);
                        sessionStorage.removeItem('temp_client_token');
                        sessionStorage.removeItem('temp_client_username');
                    }
                } else {
                    // Clear any stale temporary tokens if we're not in temporary access mode
                    if (tempToken && !window.location.search.includes('temp_access=true')) {
                        sessionStorage.removeItem('temp_client_token');
                        sessionStorage.removeItem('temp_client_username');
                    }
                    
                    // console.log('🔒 No temporary access detected:', {
                    //     hasTempToken: !!tempToken,
                    //     hasTempAccess: window.location.search.includes('temp_access=true'),
                    //     searchParams: window.location.search
                    // });
                }

                // Check if we have stored auth data
                if (token && userDataStr && sessionId) {
                    // CRITICAL: Check if this is a fresh login
                    // If so, clear the flag and ensure we use the fresh data
                    const isFreshLogin = sessionStorage.getItem('freshLogin') === 'true';
                    if (isFreshLogin) {
                        console.log('✅ Fresh login detected - using new session data');
                        sessionStorage.removeItem('freshLogin');
                    }
                    
                    // Validate session ID matches current browser context
                    if (sessionId !== currentSessionId) {
                        // This is a different browser context (e.g., incognito window)
                        // Allow this context to use stored credentials but update session ID
                        sessionStorage.setItem('sessionId', currentSessionId);
                    }

                    // Enhanced session validation - but be more lenient during login flow
                    // Skip validation if we just logged in (within last 5 seconds)
                    const lastLoginTime = sessionStorage.getItem('lastLoginTime');
                    const now = Date.now();
                    const timeSinceLogin = lastLoginTime ? now - parseInt(lastLoginTime) : 0;
                    
                    if (timeSinceLogin < 5000) {
                        // Recent login detected, skipping strict validation
                    } else if (!validateCurrentSession()) {
                        console.log('❌ Session security validation failed - clearing auth');
                        forceSessionCleanup();
                        // Determine redirect based on current URL path
                        const currentPath = window.location.pathname;
                        
                        // CRITICAL: Check if we're on a company-specific route before redirecting
                        const pathParts = currentPath.split('/');
                        const companySlug = pathParts[1];
                        const isCompanySpecific = companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password', 'thank-you'].includes(companySlug);
                        
                        let redirectTo;
                        // Check if we have a saved login URL for this company
                        const savedLoginUrl = sessionStorage.getItem('lastLoginUrl');
                        if (savedLoginUrl && companySlug && isCompanySpecific) {
                            // Extract company slug from saved URL and redirect directly to company login
                            const savedPathParts = savedLoginUrl.split('/');
                            const savedCompanySlug = savedPathParts[1];
                            if (savedCompanySlug === companySlug) {
                                redirectTo = `/${companySlug}/login`;
                            } else {
                                redirectTo = `/${companySlug}/login`;
                            }
                        } else if (isCompanySpecific) {
                            redirectTo = `/${companySlug}/login`;
                        } else {
                            redirectTo = currentPath.includes('/client/') ? '/client-signup' : '/admin/login';
                        }
                        
                        clearAuthData(redirectTo);
                        return;
                    }

                    // Validate token format and user data
                    try {
                        const userData = JSON.parse(userDataStr);
                        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
                        // console.log('[DEBUG] Restoring session:', { userData, tokenPayload });
                        // Security: Verify token hasn't been tampered with
                        if (userData.id && tokenPayload.id === userData.id) {
                            // Skip backend validation for now to prevent logout on refresh
                            // const isValidSession = await validateSession(token);
                            
                            // if (isValidSession) {
                                // CRITICAL FIX: Ensure companyId is preserved from JWT token
                                if (tokenPayload.companyId) {
                                    userData.companyId = tokenPayload.companyId;
                                    userData.companyName = tokenPayload.companyName || userData.companyName;
                                }
                                setUser(userData);
                                setIsAuthenticated(true);
                                setLoading(false);
                        
                            // } else {
                            //     console.log('❌ Session validation failed - clearing auth');
                            //     // Determine redirect based on current URL path
                            //     const currentPath = window.location.pathname;
                            //     const redirectTo = currentPath.includes('/client/') ? '/client/login' : '/admin/login';
                            //     clearAuthData(redirectTo);
                            //     setLoading(false);
                            // }
                        } else {
                            // console.log('[DEBUG] User ID mismatch during session restore:', { userData, tokenPayload });
                            setLoading(false);
                        }
                    } catch (error) {
                        console.error('Error parsing stored user/token data:', error);
                        forceSessionCleanup();
                        const currentPath = window.location.pathname;
                        
                        // CRITICAL: Check if we're on a company-specific route before redirecting
                        const pathParts = currentPath.split('/');
                        const companySlug = pathParts[1];
                        const isCompanySpecific = companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password', 'thank-you'].includes(companySlug);
                        
                        let redirectTo;
                        // Check if we have a saved login URL for this company
                        const savedLoginUrl = sessionStorage.getItem('lastLoginUrl');
                        if (savedLoginUrl && companySlug && isCompanySpecific) {
                            // Extract company slug from saved URL and redirect directly to company login
                            const savedPathParts = savedLoginUrl.split('/');
                            const savedCompanySlug = savedPathParts[1];
                            if (savedCompanySlug === companySlug) {
                                redirectTo = `/${companySlug}/login`;
                            } else {
                                redirectTo = `/${companySlug}/login`;
                            }
                        } else if (isCompanySpecific) {
                            redirectTo = `/${companySlug}/login`;
                        } else {
                            redirectTo = currentPath.includes('/client/') ? '/client-signup' : '/admin/login';
                        }
                        
                        clearAuthData(redirectTo);
                        setLoading(false);
                    }
                } else {
                    setLoading(false);
                }
            } catch (error) {
                console.error('Error during auth initialization:', error);
                setLoading(false);
            }
        };

        initializeAuth();
    }, [currentSessionId]);

    // Clear all authentication data
    const clearAuthData = (redirectTo?: string) => {
        // console.log('[DEBUG] clearAuthData called with redirectTo:', redirectTo);
        
        // Clear from both sessionStorage and localStorage for security
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('sessionId');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('sessionId');
        setUser(null);
        setIsAuthenticated(false);
        
        // If a redirect path is specified, redirect there
        if (redirectTo) {
            // console.log('[DEBUG] About to redirect to:', redirectTo);
            window.location.href = redirectTo;
        } else {
            // console.log('[DEBUG] No redirect specified, staying on current page');
        }
    };

    // Handle session conflicts (when another tab/window logs in)
    // DISABLED: This was causing false positives and immediate logout after login
    // The storage event fires for all storage changes, including same-user login
    /*
    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            // Only handle sessionId changes
            if (event.key === 'sessionId') {
                const newSessionId = event.newValue;
                const oldSessionId = event.oldValue;
                
                // Skip if this is the same session being updated (same user login)
                if (newSessionId === oldSessionId || !newSessionId || !oldSessionId) {
                    return;
                }
                
                // Check if this is actually a different user (different sessionId format)
                // Session IDs are generated with timestamp + random string, so different users will have different IDs
                if (newSessionId !== currentSessionId) {
                    
                    // Another browser context logged in, this session is now invalid
                    // Determine redirect based on current URL path
                    const currentPath = window.location.pathname;
                    
                    // CRITICAL: Check if we're on a company-specific route before redirecting
                    const pathParts = currentPath.split('/');
                    const companySlug = pathParts[1];
                    const isCompanySpecific = companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password', 'thank-you'].includes(companySlug);
                    
                    let redirectTo;
                    // Check if we have a saved login URL for this company
                    const savedLoginUrl = sessionStorage.getItem('lastLoginUrl');
                    if (savedLoginUrl && companySlug && isCompanySpecific) {
                        // Extract company slug from saved URL and redirect directly to company login
                        const savedPathParts = savedLoginUrl.split('/');
                        const savedCompanySlug = savedPathParts[1];
                        if (savedCompanySlug === companySlug) {
                            redirectTo = `/${companySlug}/login?reason=session_conflict`;
                        } else {
                            redirectTo = `/${companySlug}/login?reason=session_conflict`;
                        }
                    } else if (isCompanySpecific) {
                        redirectTo = `/${companySlug}/login?reason=session_conflict`;
                    } else {
                        redirectTo = currentPath.includes('/client/') ? '/client-signup?reason=session_conflict' : '/admin/login?reason=session_conflict';
                    }
                    
                    clearAuthData(redirectTo);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [currentSessionId]);
    */

    // Cleanup on page unload
    useEffect(() => {
        const handleBeforeUnload = async () => {
            if (isAuthenticated && user) {
            
                
                const token = sessionStorage.getItem('token');
                if (token) {
                    // Check if token is still valid before attempting invalidation
                    try {
                        const payload = JSON.parse(atob(token.split('.')[1]));
                        const isExpired = payload.exp && payload.exp < Date.now() / 1000;
                        
                        if (!isExpired) {
                            const invalidateEndpoint = `${getApiUrl()}/api/auth/invalidate-session`;
                            
                            // Use synchronous XMLHttpRequest for reliability during page unload
                            const xhr = new XMLHttpRequest();
                            xhr.open('POST', invalidateEndpoint, false);
                            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                            xhr.setRequestHeader('Content-Type', 'application/json');
                            xhr.send(JSON.stringify({}));
                            
                            // Don't log 401 or 200 responses as they're expected
                            if (xhr.status !== 401 && xhr.status !== 200) {
                                console.error('❌ Error during page unload session invalidation:', xhr.status, xhr.statusText);
                            }
                        }
                    } catch (error) {
                        // Token is invalid/expired, which is expected - don't log as error
                    }
                }
            }
        };

        // Also handle visibility change (when tab becomes hidden)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden' && isAuthenticated && user) {
                const token = sessionStorage.getItem('token');
                if (token) {
                    // Check if token is still valid before attempting invalidation
                    try {
                        const payload = JSON.parse(atob(token.split('.')[1]));
                        const isExpired = payload.exp && payload.exp < Date.now() / 1000;
                        
                        if (!isExpired) {
                            const invalidateEndpoint = `${getApiUrl()}/api/auth/invalidate-session`;
                            
                            // Use XMLHttpRequest instead of sendBeacon to properly send Authorization header
                            const xhr = new XMLHttpRequest();
                            xhr.open('POST', invalidateEndpoint, false);
                            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                            xhr.setRequestHeader('Content-Type', 'application/json');
                            xhr.send(JSON.stringify({}));
                            
                            // Don't log 401 or 200 responses as they're expected
                            if (xhr.status !== 401 && xhr.status !== 200) {
                                console.error('❌ Error during visibility change session invalidation:', xhr.status, xhr.statusText);
                            }
                        }
                    } catch (error) {
                        // Token is invalid/expired, which is expected - don't log as error
                    }
                }
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isAuthenticated, user]);

    const login = async (token: string, redirectPath?: string) => {
        if (!token) {
            console.error('❌ No token provided');
            return;
        }

        try {
            // Parse and validate the JWT payload
            const parts = token.split('.');
            if (parts.length !== 3) {
                throw new Error('Invalid token format');
            }

            const payload = JSON.parse(atob(parts[1]));
        

            // Create user object
            const userData = {
                id: payload.id,
                username: payload.username || '',
                name: payload.name || '',
                email: payload.email || '',
                role: payload.role, // Do not default to admin!
                companyName: payload.companyName || '',
                themePreference: payload.themePreference || 'light',
                branding: payload.branding || {}
            };
            // console.log('[DEBUG] Login userData:', userData);

            // Security: COMPLETELY clear ALL existing sessions and storage
            console.log('🧹 Completely clearing all browser storage...');
            sessionStorage.clear();
            localStorage.clear();
            
            // Also clear any cookies that might contain auth data
            document.cookie.split(";").forEach(function(c) { 
                document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
            });
            
            // CRITICAL: Clear IndexedDB and other storage APIs
            try {
                // Clear all IndexedDB databases
                if (window.indexedDB && window.indexedDB.databases) {
                    window.indexedDB.databases().then((dbs) => {
                        dbs.forEach((db) => {
                            if (db.name) {
                                window.indexedDB.deleteDatabase(db.name);
                            }
                        });
                    });
                }
            } catch (e) {
                // IndexedDB clearing failed, but continue
            }
            
            // CRITICAL: Set a flag to indicate this is a fresh login
            // This prevents old cached data from being used
            sessionStorage.setItem('freshLogin', 'true');
            
            // Set new token and user data
            sessionStorage.setItem('token', token);
            sessionStorage.setItem('user', JSON.stringify(userData));
            sessionStorage.setItem('sessionId', currentSessionId);
            sessionStorage.setItem('lastLoginTime', Date.now().toString()); // Track last login time
            
            // Store the current login URL for future "back to login" navigation
            const currentPath = window.location.pathname;
            // console.log('[DEBUG] Login - current path:', currentPath);
            if (currentPath.includes('/login')) {
                sessionStorage.setItem('lastLoginUrl', currentPath);
                // console.log('[DEBUG] Login - set lastLoginUrl to:', currentPath);
            } else {
                // console.log('[DEBUG] Login - current path does not contain /login, not setting lastLoginUrl');
            }

            // Patch: Only include fields defined in the User type
            setUser({
                _id: payload.id || '',
                id: payload.id,
                username: payload.username || '',
                email: payload.email || '',
                role: payload.role,
                companyId: payload.companyId || '',
                companyName: payload.companyName || '',
                clientName: payload.clientName || '',
                isActive: typeof payload.isActive === 'boolean' ? payload.isActive : true,
                createdAt: payload.createdAt || '',
                themePreference: payload.themePreference || 'light',
                branding: payload.branding || {}
            });
            setIsAuthenticated(true);

            // Redirect to user-specific URL
            if (redirectPath) {
                window.location.href = redirectPath;
            } else {
                // Check if we're in a company-specific context
                const currentPath = window.location.pathname;
                const pathParts = currentPath.split('/');
                const companySlug = pathParts[1]; // First part after domain
                
                // Determine if this is an actual admin user or a client user with admin role
                // Client users have companyName, actual admin users don't
                const isActualAdmin = userData.role === 'admin' && !userData.companyName;
                
                let userSpecificPath;
                
                if (isActualAdmin) {
                    // Admin users go to admin routes
                    userSpecificPath = `/admin/${userData.username}/dashboard`;
                } else if (companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password'].includes(companySlug)) {
                    // Company-specific context - redirect to company-specific route
                    userSpecificPath = `/${companySlug}/${userData.username}/dashboard`;
                    // console.log('[DEBUG] Company-specific context detected, redirecting to:', userSpecificPath);
                } else {
                    // Generic client context
                    // Fallback to signup since generic client routes are blocked
                    userSpecificPath = `/client-signup`;
                }
                
                // console.log('[DEBUG] Redirecting to:', userSpecificPath, '(isActualAdmin:', isActualAdmin, ', companySlug:', companySlug, ')');
                window.location.href = userSpecificPath;
            }
        } catch (error) {
            console.error('❌ Error during login:', error);
            clearAuthData();
            throw new Error('Login failed - invalid token');
        }
    };

    const logout = async () => {
        // console.log('[DEBUG] Logout started');
        
        // Store the current path for redirect analysis
        const currentPath = window.location.pathname;
        
        // Clear auth state immediately to prevent React Router issues
        setUser(null);
        setIsAuthenticated(false);
        
        // Clear all storage immediately
        sessionStorage.clear();
        localStorage.clear();
        
        // Clear cookies
        document.cookie.split(";").forEach(function(c) { 
            document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
        });
        
        // Determine redirect destination
        let redirectUrl: string;
        
        // Check if we're on a company-specific route
        const pathParts = currentPath.split('/');
        const companySlug = pathParts[1];
        const isCompanySpecific = companySlug && 
            !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password', 'thank-you'].includes(companySlug);
        
        if (isCompanySpecific) {
            // Company-specific context - redirect to company login
            redirectUrl = `/${companySlug}/login`;
        } else if (currentPath.includes('/client/')) {
            // Generic client context
            redirectUrl = '/client-signup';
        } else if (currentPath.includes('/admin/')) {
            // Admin context
            redirectUrl = '/admin/login';
        } else {
            // Default fallback
            redirectUrl = '/admin/login';
        }
        
        // console.log('[DEBUG] Logout redirect URL:', redirectUrl);
        
        // Attempt to invalidate session on backend (but don't wait for it)
        try {
            // Get token before clearing storage
            const token = sessionStorage.getItem('token') || sessionStorage.getItem('temp_client_token');
            if (token) {
                // Determine logout endpoint based on context
                const isClientContext = currentPath.includes('/client/') || isCompanySpecific;
                const logoutEndpoint = isClientContext
                    ? `${getApiUrl()}/api/auth/client/logout`
                    : `${getApiUrl()}/api/auth/admin/logout`;
                
                // Send logout request in background (don't wait for response)
                fetch(logoutEndpoint, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({})
                }).catch(error => {
                    // Ignore errors during logout
                    // console.log('[DEBUG] Background logout request failed:', error);
                });
            }
        } catch (error) {
            // Ignore errors during logout
            // console.log('[DEBUG] Error during background logout:', error);
        }
        
        // Redirect after a brief delay to ensure cleanup is complete
        setTimeout(() => {
            window.location.href = redirectUrl;
        }, 100);
    };

    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            user,
            loading,
            login,
            logout
        }}>
            {children}
        </AuthContext.Provider>
    );
};